import React, { useState, useMemo } from 'react';
import { PopperPlacementType } from '@mui/material';
import { DateTime } from 'luxon';
import SlCalendarPicker, { CalendarDate } from 'src/components/BeaconV2Components/common/SlCalendarPicker/SlCalendarPicker';
import { useAppSelector } from 'src/utils/Hooks';

import { getFirstWeekFirstYear, getLastWeek, getWeekFirstDate } from 'src/utils/dateformatting';
import { getStartAndEndOfWeek } from 'src/utils/dateUtils';
import { getSearchAppName } from 'src/utils/app';
import { AppName } from 'sl-api-connector';

/**
 * Wrapper containing logic for SlCalendarPicker for custom application time range filters
 */
const AppCalendarContainer = ({
  handleCustomDateRangeChange,
  open,
  setCalendarOpen,
  anchorEl,
  placement
}: {
  handleCustomDateRangeChange: ({ startDate, endDate }: { startDate: Date; endDate: Date }) => void;
  open: boolean;
  setCalendarOpen: (b: boolean) => void;
  anchorEl?: HTMLElement | null;
  placement?: PopperPlacementType;
}) => {
  const retailer = useAppSelector((state) => state.retailer);
  const allWeekIdsByRetailerId = useAppSelector((state) => state.allWeekIdsByRetailerId);
  const mainTimePeriod = useAppSelector((state) => state.mainTimePeriod);
  const { startWeekStartDate, endWeekEndDate, id: timePeriodType } = mainTimePeriod;

  const preFilledDates = useMemo<{ start: Date | null; end: Date | null }>(() => {
    if (timePeriodType !== 'cd') {
      return { start: null, end: null };
    }
    // startWeekStartDate and endWeekEndDate are typed as strings but are actually Date objects at runtime
    const start = (startWeekStartDate as unknown as Date) ?? null;
    const end = (endWeekEndDate as unknown as Date) ?? null;
    return { start, end };
  }, [timePeriodType, startWeekStartDate, endWeekEndDate]);

  /**
   * Selected date range state
   */
  const [selectedDateRange, setSelectedDateRange] = useState<[Date | null, Date | null]>([
    preFilledDates.start,
    preFilledDates.end
  ]);

  /**
   * Minimum available calendar date
   */
  const minDate: Date = getWeekFirstDate(getFirstWeekFirstYear(allWeekIdsByRetailerId[+retailer.id]));

  /**
   * Maximum available calendar date
   * - For Advertising: yesterday (only current date is disabled)
   * - For other apps: end date of the last available week
   */
  const isAdvertising = getSearchAppName() === AppName.Advertising;
  const lastWeekId = getLastWeek(allWeekIdsByRetailerId[+retailer.id]);
  const { endDate: lastWeekEndDate } = getStartAndEndOfWeek(lastWeekId);

  const maxDate: Date = useMemo(() => {
    if (isAdvertising) {
      // For advertising, only disable today - allow up to yesterday
      return DateTime.now().minus({ days: 1 }).startOf('day').toJSDate();
    }
    // For other apps, use the end of the last available week
    return DateTime.fromFormat(lastWeekEndDate || '', 'yyyyMMdd').toJSDate();
  }, [isAdvertising, lastWeekEndDate]);

  const shouldDisableDate = (calendar: CalendarDate): boolean => {
    const { date: calendarDate } = calendar;
    const calendarDateTime = DateTime.fromJSDate(calendarDate);
    const maxDateTime = DateTime.fromJSDate(maxDate);
    const minDateTime = DateTime.fromJSDate(minDate);

    return calendarDateTime > maxDateTime || calendarDateTime < minDateTime;
  };

  /**
   * Normalizes a date to noon local time to avoid timezone edge cases at midnight.
   * Using local time (not UTC) ensures consistency with dateToSDSWeekId which uses
   * local time methods (getFullYear, getMonth, getDate).
   */
  const normalizeDate = (date: Date | null): Date | null => {
    if (!date) return null;
    return new Date(date.getFullYear(), date.getMonth(), date.getDate(), 12, 0, 0, 0);
  };

  const isSameDay = (date1: Date | null, date2: Date | null): boolean => {
    if (!date1 || !date2) return false;
    return DateTime.fromJSDate(date1).hasSame(DateTime.fromJSDate(date2), 'day');
  };

  const handleDateChange = (dates: [Date | null, Date | null]) => {
    const [selectedStartDate, selectedEndDate] = dates;
    const newStartDate = normalizeDate(selectedStartDate);
    const newEndDate = normalizeDate(selectedEndDate);

    if (selectedDateRange[0] && selectedDateRange[1] && !isSameDay(newStartDate, selectedDateRange[0])) {
      setSelectedDateRange([newStartDate, null]);
    } else {
      setSelectedDateRange([newStartDate, newEndDate]);
    }
  };

  const handleCalendarClose = () => {
    const [selectedStartDate, selectedEndDate] = selectedDateRange;
    setCalendarOpen(false);

    if (selectedStartDate && selectedEndDate) {
      handleCustomDateRangeChange({ startDate: selectedStartDate, endDate: selectedEndDate });
    }
  };

  return (
    <SlCalendarPicker
      open={open}
      onClose={handleCalendarClose}
      selectedDateRange={selectedDateRange}
      handleDateChange={handleDateChange}
      shouldDisableDate={shouldDisableDate}
      anchorEl={anchorEl}
      placement={placement}
    />
  );
};

export default AppCalendarContainer;
